import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { LandlordDetailUIModel } from '../../../shared/ui-models/landlord.ui-model';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../shared/components/core-ui/button/button.component';
import { getLandlordDetail } from '../../data.service';
import BankAccountCard from './components/bank-account-card/bank-account-card.component';

const AUDIT_ACTION_LABEL: { [action: string]: string } = {
  created: 'Bank account created',
  updated: 'Bank account updated'
};

function formatTimestamp(value: string): string {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
}

export const LandlordDetailsContainer: React.FC = () => {
  const { landlordId } = useParams<{ landlordId: string }>();
  const history = useHistory();

  const [detail, setDetail] = useState<LandlordDetailUIModel | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState<number>(0);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setHasError(false);

    getLandlordDetail(landlordId).then((response) => {
      if (!isActive) {
        return;
      }
      if (isErrorInfo(response)) {
        setHasError(true);
        setDetail(null);
      } else {
        setDetail(response);
      }
      setIsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [landlordId, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6 h-8 w-64 animate-pulse rounded bg-gray-200" />
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1, 2, 3].map((card) => (
            <div key={card} className="h-40 animate-pulse rounded-2xl bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  if (hasError || !detail) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
          <div className="mb-3 text-gray-700">This landlord could not be loaded</div>
          <Button type={ButtonType.default} size={ButtonSize.small} onClick={reload}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <button
        className="mb-4 text-sm text-gray-500 hover:text-gray-700"
        onClick={() => history.push(APP_ROUTES.LANDLORD_MODULE.FULL_PATH)}
      >
        Back to landlords
      </button>

      <h1 className="mb-6 text-2xl font-semibold text-gray-800">{detail.name || 'Landlord'}</h1>

      <div className="grid gap-6 md:grid-cols-2">
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">Profile</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Reference</div>
              <div className="text-sm text-gray-800">{detail.landlordId}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Email</div>
              <div className="text-sm text-gray-800">{detail.email || '-'}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Phone</div>
              <div className="text-sm text-gray-800">{detail.phone || '-'}</div>
            </div>
          </div>
        </section>

        <BankAccountCard
          config={{ landlordId: detail.landlordId, bankAccount: detail.bankAccount }}
          eventHandlers={{ savedHandler: reload }}
        />

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">
            Properties and units ({detail.units.length})
          </h2>
          {detail.units.length === 0 && <p className="text-sm text-gray-500">No units assigned</p>}
          {detail.units.length > 0 && (
            <div className="max-h-64 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-gray-600">
                  <tr>
                    <th className="py-2 font-medium">Unit</th>
                    <th className="py-2 font-medium">Property</th>
                    <th className="py-2 font-medium">Area</th>
                    <th className="py-2 font-medium">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.units.map((unit, index) => (
                    <tr key={`${unit.id}-${index}`} className="border-t border-gray-100">
                      <td className="py-2 text-gray-800">{unit.name || '-'}</td>
                      <td className="py-2 text-gray-600">{unit.propertyName || '-'}</td>
                      <td className="py-2 text-gray-600">{unit.areaName || '-'}</td>
                      <td className="py-2 text-gray-600">{unit.landlordShare || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">Activity</h2>
          {detail.audit.length === 0 && (
            <p className="text-sm text-gray-500">No changes recorded</p>
          )}
          {detail.audit.length > 0 && (
            <ul className="flex max-h-64 flex-col gap-4 overflow-auto">
              {detail.audit.map((entry, index) => (
                <li
                  key={`${entry.performedAt}-${index}`}
                  className="border-l-2 border-gray-200 pl-3"
                >
                  <div className="text-sm font-medium text-gray-800">
                    {AUDIT_ACTION_LABEL[entry.action] || entry.action}
                    {entry.source === 'import' ? ' via import' : ''}
                  </div>
                  <div className="text-xs text-gray-500">
                    {entry.performedBy || 'Unknown'} · {formatTimestamp(entry.performedAt)}
                  </div>
                  {entry.changes.length > 0 && (
                    <ul className="mt-1 text-xs text-gray-600">
                      {entry.changes.map((change, changeIndex) => (
                        <li key={`${change.field}-${changeIndex}`}>
                          {change.field}: {change.oldValue || 'empty'} → {change.newValue}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default LandlordDetailsContainer;
